import {
	apiQueryKey,
	useApiGetEntityCounts,
	useApiHasPreviewAccess,
	useRetrieveQualityEntitySuggestions,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	queryClient,
	useAuthUser,
	useDataQualityAccess,
	useIntegration,
	useIntegrationSupport,
	usePolicyEnabled,
	useSuspenseFeatureAccess,
	useUpdateSecodaEntity,
} from '../../api';
import { usePolicyReadPermission } from '../../api/hooks/policy';
import useSuspenseSecodaEntity from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import { EntityAccessControlList } from '../../components/AccessControl/EntityAccessControlList';
import { DataQualityTabV2 } from '../../components/DataQualityScore/DataQualityTab/DataQualityTab';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import { getDefaultTab } from '../../components/EntityPageLayout/utils';
import EntityPolicyList from '../../components/EntityPolicyList/EntityPolicyList';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import TableDataPreview from '../../components/Preview/TableDataPreview';
import { QueryList } from '../../components/QueryListV2/QueryList';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import Tests from '../TablePage/Tests';
import type { DjangoValueType } from '../TemplatePage/types';
import ColumnsTab from './TableEntityTabs/ColumnsTab';
import { DataQualityTab } from './TableEntityTabs/DataQualityTab';
import { DllTab } from './TableEntityTabs/DdlTab';
import IncidentsTab from './TableEntityTabs/IncidentsTab';
import MonitorsTab from './TableEntityTabs/MonitorsTab';

const InnerTableEntityPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const hasPolicyAccess = usePolicyEnabled();
	const dqsEnabled = useDataQualityAccess();
	const navigateToTab = useTabNavigate();
	const { dqsV2 } = useFeatureFlags();
	const {
		lineageAccess,
		monitoringAccess,
		resourcePreviewAccess,
		secodaCoreAccess,
	} = useSuspenseFeatureAccess();

	const { hasPermission: hasQueriesAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Queries.Read',
	});

	const { hasPermission: hasQuestionsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Questions.Read',
	});

	const { hasPermission: hasLineageReadPermission } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Lineage.Read',
	});

	const { hasPermission: hasMonitorReadPermission } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Monitors.Read',
	});

	const { hasPermission: hasDQSReadPermission } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'DQS.Read',
	});

	const { hasPermission: hasTestsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Tests.Read',
	});

	const hasPolicyReadPermission = usePolicyReadPermission();

	const { data: tableEntity } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('table_entity/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSecodaEntity({
		options: {
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: apiQueryKey('quality'),
				}),
		},
	});

	const updateTableEntity = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				await mutateAsync({
					data: {
						id: tableEntity!.id,
						[key]: value,
					},
				});
				trackEvent('table/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, tableEntity, user, workspace]
	);

	const canShowDataQualityScore =
		dqsEnabled && tableEntity?.entity_type?.toLowerCase() === 'table';
	const { data: dataQuality } = useRetrieveQualityEntitySuggestions(
		{
			pathParams: {
				entityId: tableEntity?.id || '',
			},
		},
		{
			enabled: !!tableEntity && canShowDataQualityScore,
			refetchOnMount: 'always',
		}
	);

	const { data: integration } = useIntegration({
		id: tableEntity?.integration || '',
		options: {
			enabled: !!tableEntity?.integration,
		},
	});
	const { data: previewPermissionGranted } = useApiHasPreviewAccess(
		{
			pathParams: {
				integrationId: tableEntity?.integration ?? '',
			},
		},
		{
			enabled: !!tableEntity?.integration,
		}
	);

	const { data: entityCounts } = useApiGetEntityCounts(
		{
			queryParams: {
				entity_id: tableEntity?.id || '',
			},
		},
		{ enabled: !!tableEntity?.id }
	);

	const { data: integrationSupport } = useIntegrationSupport();
	const integrationKeys = integrationSupport
		? Object.keys(integrationSupport)
		: [];
	const isMonitoringEnabled =
		integrationKeys.includes(integration?.type ?? '') &&
		!!workspace?.monitoring_enabled;

	const { entityPrivilegesTab } = useFeatureFlags();

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('table/lineage/open', {}, user, workspace);
			}
			if (updatedTab === 'quality') {
				trackEvent('dqs/resource_tab/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	if (!tableEntity) {
		return <EntityPageLayoutSkeleton />;
	}

	const showPreviewTab =
		integration?.support_read_table &&
		!!previewPermissionGranted &&
		!!resourcePreviewAccess;

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			tableEntity.id,
			tableEntity.published,
			EntityType.table,
			tableEntity.native_type
		);

	const tabs = useMemo(
		() => [
			{
				value: 'columns',
				label: 'Columns',
				component: <ColumnsTab table={tableEntity} />,
			},
			{
				value: 'semantic-views',
				label: 'Semantic Views',
				condition: tableEntity.native_type === 'semantic_view',
				component: (
					<ColumnsTab table={tableEntity} entityType={EntityType.generic} />
				),
			},

			{
				value: 'lineage',
				label: 'Lineage',
				condition: hasLineageReadPermission && !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'monitors',
				label: 'Monitors',
				condition:
					isMonitoringEnabled && hasMonitorReadPermission && !!monitoringAccess,
				component: (
					<MonitorsTab
						integrationId={tableEntity.integration}
						tableId={tableEntity.id}
					/>
				),
			},
			{
				value: 'incidents',
				label: 'Incidents',
				condition:
					isMonitoringEnabled &&
					hasMonitorReadPermission &&
					Boolean(entityCounts?.incident_count),
				component: <IncidentsTab tableId={tableEntity.id} />,
			},
			{
				value: 'preview',
				label: 'Preview',
				condition: showPreviewTab,
				component: <TableDataPreview tableKey={tableEntity.id} />,
			},
			{
				value: 'tests',
				label: 'Tests',
				condition: hasTestsAccess,
				component: <Tests entity={tableEntity} />,
			},
			{
				value: 'usage',
				label: 'Usage',
				condition: hasQueriesAccess && !!secodaCoreAccess,
				component: <QueryList entity={tableEntity} />,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: hasQuestionsAccess && !!secodaCoreAccess,
				component: <EntityDiscussions entity={tableEntity} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={tableEntity} />,
			},
			{
				value: 'policies',
				label: 'Policies',
				condition: hasPolicyAccess && hasPolicyReadPermission,
				component: <EntityPolicyList entity={tableEntity} />,
			},
			{
				value: 'quality',
				label: 'Quality',
				condition:
					(!!dataQuality?.score && hasDQSReadPermission) ||
					(dqsV2 && hasDQSReadPermission),
				component: dqsV2 ? (
					<DataQualityTabV2 entity={tableEntity} />
				) : (
					<DataQualityTab entity={tableEntity} />
				),
			},
			{
				value: 'privileges',
				label: 'Privileges',
				condition: entityPrivilegesTab,
				component: <EntityAccessControlList entity={tableEntity} />,
			},
			{
				value: 'ddl',
				label: 'Definition',
				condition: tableEntity.native_type === 'semantic_view',
				component: <DllTab table={tableEntity} />,
			},
		],
		[
			tableEntity,
			hasLineageReadPermission,
			lineageAccess,
			lineageFlowStore,
			lineageFlowUIStore,
			lineageFilterStore,
			isMonitoringEnabled,
			hasMonitorReadPermission,
			monitoringAccess,
			entityCounts?.incident_count,
			showPreviewTab,
			hasTestsAccess,
			hasQueriesAccess,
			secodaCoreAccess,
			hasQuestionsAccess,
			hasPolicyAccess,
			hasPolicyReadPermission,
			dataQuality?.score,
			hasDQSReadPermission,
			dqsV2,
			entityPrivilegesTab,
		]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(tabs), [tabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={tableEntity.id}
			name="Table"
			isReadOnlyTitle
			entity={tableEntity}
			icon={<EntityLogo entity={tableEntity} size={32} />}
			updateEntity={updateTableEntity}
			withDescription
			withEntityByteSize
			withEntityRowCount
			{...getEntityPageSidebarProps(EntityType.table)}
		>
			<OverviewPageTabs
				key={tableEntity.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={tabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// by memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function TableEntityPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerTableEntityPage id={id} />;
}
